import { CloudUpload } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid2,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import styled from "styled-components";

/*
 *   Available Inputs:
 *   SWITCH, SELECT, TEXT, NUMBER, SLIDER, IMAGE
 *
 *   Options needed:
 *   SELECT: Either { key: value, ...} or [{title: "", ...}, ...]
 *   SLIDER: 'min', 'max' & 'step' (increment steps)
 *   NUMBER: 'min', 'max' & 'step' (increment steps)
 */
const PropInput = ({
  name,
  pageId,
  inputType,
  options,
  prop,
  handleChange,
}) => {
  const MIN = options && options.min ? options.min : 0;
  const MAX = options && options.max ? options.max : 0;

  const handleNumberBlur = (event) => {
    if (event.target.value < MIN) {
      handleChange(parseInt(MIN));
    } else if (event.target.value > MAX) {
      handleChange(parseInt(MAX));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "image/svg+xml" ||
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png")
    ) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Bitte laden Sie nur SVG, JPEG, JPG oder PNG Dateien hoch.");
    }
  };

  const getInput = (type) => {
    switch (type) {
      case "SWITCH":
        return (
          <FormControlLabel
            control={
              <Switch
                checked={prop}
                onChange={(event) => handleChange(event.target.checked)}
                slotProps={{ input: { "aria-label": "controlled" } }}
              />
            }
            label={name}
          />
        );

      case "SELECT":
        return (
          <>
            {options &&
              (!Array.isArray(options) ? (
                Object.keys(options).length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel id={`${pageId}-${name}-select-label`}>
                      {name}
                    </InputLabel>
                    <Select
                      labelId={`${pageId}-${name}-select-label`}
                      id={`${pageId}-${name}-select-input`}
                      value={prop ? prop : ""}
                      label={name}
                      onChange={(event) => handleChange(event.target.value)}
                    >
                      <MenuItem disabled value="">
                        {name} auswählen
                      </MenuItem>
                      {Object.entries(options).map(([key, value], index) => (
                        <MenuItem
                          key={`${pageId}-${name}-select-option-${index}`}
                          value={value}
                        >
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              ) : (
                <FormControl fullWidth>
                  <InputLabel id={`${pageId}-${name}-select-label`}>
                    {name}
                  </InputLabel>
                  <Select
                    labelId={`${pageId}-${name}-select-label`}
                    id={`${pageId}-${name}-select-input`}
                    value={
                      prop
                        ? options.findIndex(
                            (option) => option.title === prop.title
                          )
                        : ""
                    }
                    label={name}
                    onChange={(event) =>
                      handleChange(options[event.target.value])
                    }
                  >
                    <MenuItem disabled value="">
                      {name} auswählen
                    </MenuItem>
                    {options.map((option, index) => (
                      <MenuItem
                        key={`${pageId}-${name}-select-option-${index}`}
                        value={index}
                      >
                        {option.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
          </>
        );

      case "TEXT":
        return (
          <TextField
            variant="outlined"
            label={name}
            placeholder={name}
            value={prop ? prop : ""}
            onChange={(event) => handleChange(event.target.value)}
            fullWidth
          />
        );

      case "NUMBER":
        return (
          <Box direction="row">
            <Typography component="span" sx={{ mr: 1 }}>
              {name}
            </Typography>
            <Input
              inputProps={{
                step: options.step ? options.step : 1,
                min: MIN,
                max: MAX,
                type: "number",
              }}
              value={prop ? prop : 0}
              size="medium"
              onChange={(event) => handleChange(parseInt(event.target.value))}
              onBlur={handleNumberBlur}
            />
          </Box>
        );

      case "SLIDER":
        return (
          <Box sx={{ m: 2, mt: 0, mb: 0 }}>
            <Typography component="span" gutterBottom>
              {name}
            </Typography>
            <Slider
              aria-label={name}
              defaultValue={prop ? prop : 0}
              slots={{ valueLabel: ValueLabelComponent }}
              valueLabelDisplay="auto"
              min={MIN}
              max={MAX}
              step={options.step ? options.step : 1}
              onChange={(event) => handleChange(event.target.value)}
              marks
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">{MIN}</Typography>
              <Typography variant="body2">{MAX}</Typography>
            </Box>
          </Box>
        );

      case "IMAGE":
        return (
          <Grid2 container direction="column">
            <img
              src={prop}
              style={{
                maxWidth: "250px",
                border: "1px solid black",
                marginBottom: "0.5em",
              }}
              alt=""
            />
            <Button
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUpload />}
              component="label"
              sx={{ width: "fit-content" }}
            >
              {name}
              <VisuallyHiddenInput
                type="file"
                accept=".svg, .jpeg, .jpg, .png"
                onChange={(event) => handleFileChange(event)}
              />
            </Button>
          </Grid2>
        );
      default:
        return <></>;
    }
  };

  return <>{getInput(inputType)}</>;
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ValueLabelComponent = (props) => {
  const { children, value } = props;

  return (
    <Tooltip enterTouchDelay={0} placement="top" title={value}>
      {children}
    </Tooltip>
  );
};

export default PropInput;
